import { Marker as MapboxMarker } from 'mapbox-gl';
import { useRef } from 'react';
import { renderToString } from 'react-dom/server';

import { ON_CLICK_MARKER_EVENT } from './constants';
import type { Map, Marker, MarkerData } from './types';

const useMarkers = () => {
    const markerMapRef = useRef<Record<string, MarkerData>>({});
    const isClusteredRef = useRef(true);

    const addMarkers = (map?: Map, markers?: Marker[]) => {
        if (!map || !markers) {
            return;
        }

        markers.forEach(marker => {
            addMarker(map, marker);
        });
    };

    const addMarker = (map: Map, marker: Marker) => {
        const markerDiv = document.createElement('div');

        const { isStatic, markerElement } = marker;

        markerDiv.innerHTML = renderToString(markerElement);

        if (isStatic && isClusteredRef.current) {
            markerDiv.style.display = 'none';
        }

        const newMapboxMarker = new MapboxMarker({
            element: markerDiv,
            rotation: marker.rotation ?? 0,
            rotationAlignment: 'map'
        });

        newMapboxMarker.setLngLat(marker.position as [number, number]);
        newMapboxMarker.addTo(map);

        markerMapRef.current[marker.id as string] = {
            isStatic,
            marker: newMapboxMarker,
            markerContainer: markerDiv
        };

        markerDiv.onclick = (event: MouseEvent) => {
            event.preventDefault();
            event.stopPropagation();

            map.fire(ON_CLICK_MARKER_EVENT, {
                clickEvent: event,
                map,
                markerFeatureId: marker.featureId,
                markerId: marker.id,
                position: marker.position as [number, number]
            });
        };
    };

    const setMarkersVisibility = (isVisible: boolean) => {
        const display = isVisible ? '' : 'none';

        Object.values(markerMapRef.current)
            .filter(({ isStatic }) => isStatic)
            .forEach(({ markerContainer }) => {
                markerContainer.style.display = display;
            });
    };

    const updateMarkers = (map?: Map, markers?: Marker[], isClustered = false) => {
        if (!map || !markers) {
            return;
        }

        isClusteredRef.current = isClustered;
        setMarkersVisibility(!isClustered);

        markers.forEach(marker => {
            updateMarker(map, marker);
        });

        const allMarkerIds = markers.map(marker => marker.id);
        const mappedMarkerIds = Object.keys(markerMapRef.current);

        mappedMarkerIds
            .filter(markerId => !allMarkerIds.includes(markerId))
            .forEach(markerId => {
                const marker = markerMapRef.current[markerId];

                if (!marker) {
                    return;
                }

                const { marker: foundMapboxMarker } = marker;

                foundMapboxMarker.remove();
                delete markerMapRef.current[markerId];
            });
    };

    const updateMarker = (map: Map, marker: Marker) => {
        const existingMarker = markerMapRef.current[marker?.id as string];

        if (!existingMarker) {
            addMarker(map, marker);

            return;
        }

        const { marker: foundMapboxMarker, markerContainer } = existingMarker;
        const { markerElement, position, rotation } = marker;

        markerContainer.innerHTML = renderToString(markerElement);
        foundMapboxMarker.setLngLat(position as [number, number]);
        foundMapboxMarker.setRotation(rotation ?? 0);
    };

    return {
        addMarker,
        addMarkers,
        updateMarkers
    };
};

export default useMarkers;
